<script lang="ts">
export const elBubbleChatAppearences = ['filled', 'outlined'] as const;
export type ElBubbleChatAppearences = (typeof elBubbleChatAppearences)[number];

export const elBubbleChatColors = ['brand-blue', 'brand-green', 'indigo', 'purple', 'pink'] as const;
export type ElBubbleChatColors = (typeof elBubbleChatColors)[number];
</script>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import ElAvatar from './ElAvatar.vue';
import { DropdownItem } from './ElDropdown.vue';
import ElFile from './ElFile.vue';
import ElIcon from './ElIcon.vue';
import ElInputCheckbox, { ElInputCheckboxModelValues } from './forms/ElInputCheckbox.vue';
import { omit } from 'lodash';
import ElItem from './ElItem.vue';

const props = withDefaults(
  defineProps<{
    appearence: ElBubbleChatAppearences;
    color?: ElBubbleChatColors;
    time?: string;
    message?: string;
    automaticMessage?: string;
    headline?: string;
    avatar?: Pick<InstanceType<typeof ElAvatar>['$props'], 'picture' | 'label'>;
    file?: Pick<InstanceType<typeof ElFile>['$props'], 'name' | 'format' | 'url'>;
    picture?: string;
    loading?: boolean;
    error?: boolean;
    multiSelect?: boolean;
    selected?: boolean;
    maxWidth?: number;
    contextMenuItems?: DropdownItem[];
  }>(),
  {
    color: 'brand-blue',
    file: undefined,
    picture: undefined,
    automaticMessage: undefined,
    time: undefined,
    message: undefined,
    headline: undefined,
    avatar: undefined,
    maxWidth: 60,
    contextMenuItems: undefined,
  },
);

const emit = defineEmits<{
  (event: 'row-selection', value: ElInputCheckboxModelValues): void;
  (event: 'open-file', url?: string): void;
  (event: 'open-picture'): void;
}>();

const showMenu = ref<boolean>(false);
const menuX = ref<number>(0);
const menuY = ref<number>(0);

const handleShowContextMenu = (event: MouseEvent) => {
  showMenu.value = true;
  menuX.value = event.pageX;
  menuY.value = event.pageY;
};

const contextMenuElement = ref<HTMLDivElement>();

const contextMenuSize = computed(() => {
  if (!contextMenuElement.value)
    return {
      width: 0,
      height: 0,
    };

  return {
    width: contextMenuElement.value.clientWidth,
    height: contextMenuElement.value.clientHeight,
  };
});

const deviceSize = computed(() => {
  return { width: window.innerWidth, height: window.innerHeight };
});

const positionX = computed(() => {
  const totalWidth = menuX.value + contextMenuSize.value.width;

  if (totalWidth > deviceSize.value.width) return menuX.value - contextMenuSize.value.width;

  return menuX.value;
});

const positionY = computed(() => {
  const totalHeight = menuY.value + contextMenuSize.value.height;

  if (totalHeight > deviceSize.value.height) return menuY.value - contextMenuSize.value.height;

  return menuY.value;
});

const bubbleMaxWidth = computed(() => {
  let maxWidth = props.maxWidth;

  if (props.maxWidth < 10) maxWidth = 10;
  if (props.maxWidth > 100) maxWidth = 100;

  if (props.appearence === 'filled' && (props.error || props.multiSelect)) return `calc(${maxWidth}% + 2.75rem)`;

  if (props.avatar) return `calc(${maxWidth}% + 3.25rem)`;

  return `${maxWidth}%`;
});
</script>

<template>
  <div
    class="w-full flex"
    :class="{
      'justify-end': appearence === 'filled',
      'justify-start': appearence === 'outlined',
    }"
  >
    <div
      class="flex flex-col gap-2 w-fit"
      :style="{
        /* The message container width should be max 60% (default) of parent width.
        To ensure that when the checkbox for multiselect/error/avatar are visible,
        we add extra width (same as multiselect/error/user avatar width),
        so the maximum width of the message container should not change */
        'max-width': bubbleMaxWidth,
      }"
    >
      <div
        v-if="automaticMessage"
        class="text-sm font-semibold text-neutral-lighter flex"
        :class="{
          'justify-end': appearence === 'filled',
          'justify-start': appearence === 'outlined',
          'opacity-0 select-none': appearence === 'filled' && multiSelect && !error,
          'mr-11': appearence === 'filled' && error,
        }"
      >
        {{ automaticMessage }}
      </div>

      <div
        class="flex gap-5 items-center"
        :class="{
          'justify-end': appearence === 'filled',
          'justify-start': appearence === 'outlined',
        }"
      >
        <div class="flex flex-col gap-2 w-fit">
          <div class="flex gap-3">
            <ElAvatar
              v-if="avatar && appearence === 'outlined'"
              size="sm"
              :picture="avatar.picture"
              :label="avatar.label"
              class="shrink-0"
            />

            <div
              class="rounded-2xl shadow-sm p-2 flex flex-col gap-1 justify-center"
              :class="{
                'text-white': appearence === 'filled',
                'bg-brandBlue-500': appearence === 'filled' && color === 'brand-blue',
                'bg-brandGreen-500': appearence === 'filled' && color === 'brand-green',
                'bg-indigo-500': appearence === 'filled' && color === 'indigo',
                'bg-purple-500': appearence === 'filled' && color === 'purple',
                'bg-pink-500': appearence === 'filled' && color === 'pink',
                'bg-neutral-surface text-gray-900 border border-neutral-surface': appearence === 'outlined',
              }"
              @contextmenu.prevent="contextMenuItems && contextMenuItems.length && handleShowContextMenu($event)"
            >
              <div v-if="headline && appearence === 'outlined'" class="text-neutral-lighter text-xs font-medium">{{ headline }}</div>

              <div v-if="picture" class="overflow-hidden rounded-xl w-48 max-h-80 cursor-pointer" @click="emit('open-picture')">
                <img :src="picture" class="w-full h-full object-cover" />
              </div>

              <ElFile
                v-if="file"
                :variant="appearence === 'filled' ? 'alpha' : 'outlined'"
                :name="file.name"
                :format="file.format"
                class="cursor-pointer"
                @click="emit('open-file', file.url)"
              />

              <div
                v-if="message"
                class="flex text-sm font-normal supports-[overflow-wrap:anywhere]:[overflow-wrap:anywhere] supports-[not(overflow-wrap:anywhere)]:[word-break:break-word] whitespace-pre-wrap"
                :class="{
                  'text-neutral-inverse': appearence === 'filled',
                  'text-neutral-darker': appearence === 'outlined',
                }"
              >
                {{ message }}
              </div>
            </div>
          </div>
        </div>

        <ElIcon
          v-if="appearence === 'filled' && error"
          name="ExclamationCircleIcon"
          solid
          class="w-6 h-6 text-error-active flex justify-start shrink-0"
        />

        <ElInputCheckbox
          v-if="appearence === 'filled' && multiSelect && !error"
          :model-value="selected"
          :color="color"
          class="shrink-0"
          @update:model-value="val => emit('row-selection', val)"
        />
      </div>

      <!-- Context menu -->
      <div
        v-if="contextMenuItems && contextMenuItems.length && showMenu"
        class="fixed w-screen h-screen top-0 left-0 z-40 bg-transparent cursor-default"
        @click="() => (showMenu = false)"
      >
        <div
          ref="contextMenuElement"
          class="z-50 absolute bg-white rounded-md shadow p-2 min-w-52"
          :style="{ top: positionY + 'px', left: positionX + 'px' }"
        >
          <div class="flex w-full flex-col gap-1">
            <ElItem
              v-for="(item, index) in contextMenuItems"
              :id="'option-' + index"
              ref="itemRefs"
              :key="index"
              class="self-stretch"
              :class="{
                'cursor-pointer': !item.disabled,
                'cursor-default': item.disabled,
              }"
              role="option"
              tabindex="-1"
              v-bind="omit(item, 'onClick')"
              :disabled="item.disabled"
              :data-cy="`${item.text?.trim().replaceAll(' ', '').replaceAll(',', '')}-dropdown-item`"
              @click.prevent="!item.disabled && item.onClick?.()"
            />
          </div>
        </div>
      </div>

      <div
        v-if="time || loading"
        class="text-xs text-neutral-lighter flex justify-end"
        :class="{
          'opacity-0 select-none': appearence === 'filled' && (multiSelect || error),
        }"
      >
        <div v-if="loading && appearence === 'filled'">Invio in corso</div>
        <div v-else>
          {{ time }}
        </div>
      </div>
    </div>
  </div>
</template>
